import React, { useState, useEffect, useMemo, useRef } from "react";

// SheEarns Shakti Wallet: Unified reward system linking Reflect-a-Day & SkillQuest
// Features:
// - Shared Shakti Coins earned from XP (SkillQuest) + Peace Points (Reflect-a-Day)
// - Global Level + Badge tracking
// - LocalStorage persistence
// - Mobile-first responsive UI with dark theme, pink accents, smooth animations, and number counting

const STORAGE_KEY_WALLET = "sheearns_shakti_wallet_v1";
const STORAGE_KEY_REFLECT = "reflectaday_entries_v1";
const STORAGE_KEY_SKILL = "skillquest_progress_v1";

// Animated Number Component
function AnimatedNumber({ value, duration = 1000, className = "" }) {
  const [displayValue, setDisplayValue] = useState(0);
  const prevValueRef = useRef(value);
  const animationRef = useRef(null);

  useEffect(() => {
    if (value !== prevValueRef.current) {
      const startValue = prevValueRef.current;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3); // Ease out cubic

        const newValue = Math.floor(startValue + (value - startValue) * easeProgress);
        setDisplayValue(newValue);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setDisplayValue(value);
        }
      };

      animationRef.current = requestAnimationFrame(animate);
      prevValueRef.current = value;
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]);

  return <span className={className}>{displayValue.toLocaleString()}</span>;
}

export default function ShaktiWallet() {
  const [wallet, setWallet] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_WALLET);
      return raw ? JSON.parse(raw) : { coins: 0, totalXP: 0, peacePoints: 0, lastSync: null };
    } catch {
      return { coins: 0, totalXP: 0, peacePoints: 0, lastSync: null };
    }
  });

  const [syncStatus, setSyncStatus] = useState("");
  const [isSyncing, setIsSyncing] = useState(false);

  const syncData = async () => {
    setIsSyncing(true);
    setSyncStatus("🔄 Syncing...");
    try {
      // Simulate a brief delay for animation feel
      await new Promise(resolve => setTimeout(resolve, 800));

      const reflectRaw = JSON.parse(localStorage.getItem(STORAGE_KEY_REFLECT) || '{}');
      const skillRaw = JSON.parse(localStorage.getItem(STORAGE_KEY_SKILL) || '{}');

      // Peace Points sum from Reflect-a-Day
      const peacePoints = Object.values(reflectRaw).reduce((sum, e) => sum + (10 + (e.mood - 1) * 5), 0);

      // XP sum from SkillQuest
      const totalXP = Object.values(skillRaw).reduce((sum, q) => sum + (q.xp || 0), 0);

      // Shakti Coins rule: 1 coin = 5 peacePoints + 10 XP
      const coins = Math.floor(peacePoints / 5 + totalXP / 10);

      const newWallet = { coins, totalXP, peacePoints, lastSync: new Date().toISOString() };
      setWallet(newWallet);
      localStorage.setItem(STORAGE_KEY_WALLET, JSON.stringify(newWallet));
      setSyncStatus("✅ Synced successfully!");
    } catch (e) {
      setSyncStatus("❌ Sync failed.");
    } finally {
      setIsSyncing(false);
    }
  };

  const level = 1 + Math.floor(wallet.coins / 20);

  const badges = useMemo(() => {
    const list = [];
    if (wallet.coins >= 10) list.push("🏅 Consistent Learner");
    if (wallet.coins >= 30) list.push("💎 Reflective Achiever");
    if (wallet.coins >= 60) list.push("🌟 Shakti Champion");
    if (list.length === 0) list.push("✨ Start earning Shakti Coins!");
    return list;
  }, [wallet]);

  // Entrance animation trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      document.body.classList.add('animate-fadeIn');
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-pink-900/10 to-gray-800 p-4 md:p-6 text-gray-100 animate-in fade-in duration-500 slide-in-from-top-4">
      <header className="max-w-md mx-auto w-full">
        <h1 className="text-2xl md:text-3xl font-bold leading-tight text-pink-300 mb-2 animate-pulse-slow">
          💖 Shakti Wallet
        </h1>
        <p className="text-sm md:text-base mt-1 text-gray-300 mb-4 opacity-0 animate-in fade-in-50 duration-700 delay-200">
          Your unified progress tracker for Reflect-a-Day & SkillQuest
        </p>

        <section className="bg-gray-800/50 backdrop-blur-sm p-4 md:p-5 rounded-xl shadow-lg border border-pink-500/20 animate-in zoom-in duration-500 delay-300">
          <div className="flex justify-between items-center text-sm md:text-base transition-all duration-700 ease-out">
            <div className="text-gray-300 animate-slide-in-left">
              Level <AnimatedNumber value={level} duration={800} className="font-semibold text-pink-300" />
            </div>
            <div className="text-gray-300 animate-slide-in-right delay-100">
              Shakti Coins <AnimatedNumber value={wallet.coins} duration={1200} className="font-semibold text-pink-400" />
            </div>
          </div>
        </section>

        <section className="mt-4 bg-gray-800/50 backdrop-blur-sm p-4 md:p-5 rounded-xl shadow-lg border border-pink-500/20 animate-in zoom-in duration-500 delay-400">
          <h2 className="font-semibold text-lg md:text-xl mb-3 text-pink-200 animate-fade-in-up">
            💎 Combined Performance
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm md:text-base transition-all duration-1000 ease-out">
            <div className="text-center animate-bounce-subtle">
              <div className="text-xs md:text-sm text-gray-400 mb-1 transition-opacity duration-300">Total XP</div>
              <AnimatedNumber value={wallet.totalXP} duration={1500} className="text-lg md:text-2xl font-semibold text-pink-300 block" />
            </div>
            <div className="text-center animate-bounce-subtle delay-200">
              <div className="text-xs md:text-sm text-gray-400 mb-1 transition-opacity duration-300">Peace Points</div>
              <AnimatedNumber value={wallet.peacePoints} duration={1500} className="text-lg md:text-2xl font-semibold text-pink-300 block" />
            </div>
          </div>
          <div className="text-xs md:text-sm text-center text-gray-500 mt-3 transition-all duration-500">
            Last Sync: {wallet.lastSync ? new Date(wallet.lastSync).toLocaleString() : 'Never'}
          </div>
          <button
            onClick={syncData}
            disabled={isSyncing}
            className={`mt-4 w-full py-3 md:py-3.5 rounded-lg font-medium text-white shadow-lg border border-pink-500/30 transition-all duration-300 ease-out ${
              isSyncing 
                ? 'bg-gradient-to-r from-gray-600 to-gray-700 cursor-not-allowed animate-pulse' 
                : 'bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-500 hover:to-pink-600 hover:scale-105 active:scale-95 hover:shadow-pink-500/25'
            }`}
          >
            {isSyncing ? '🔄 Syncing...' : '🔄 Sync Data'}
          </button>
          {syncStatus && (
            <div className={`text-center mt-2 text-sm md:text-base transition-all duration-500 ease-in-out transform ${
              syncStatus.includes('✅') ? 'text-green-300 scale-105' : syncStatus.includes('❌') ? 'text-red-300 scale-105' : 'text-gray-300'
            } animate-fade-in-down`}>
              {syncStatus}
            </div>
          )}
        </section>

        <section className="mt-6 bg-gray-800/50 backdrop-blur-sm p-3 md:p-4 rounded-lg shadow-lg border border-pink-500/20 animate-in zoom-in duration-500 delay-500">
          <h3 className="text-sm md:text-base font-semibold mb-3 text-pink-200 animate-fade-in-up delay-100">
            🏆 Badges
          </h3>
          <ul className="text-sm md:text-base list-disc pl-4 space-y-1.5 text-gray-200 animate-in fade-in duration-700 delay-600">
            {badges.map((b, i) => (
              <li 
                key={i} 
                className="hover:text-pink-300 transition-all duration-300 ease-out hover:scale-105 hover:translate-x-1"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                {b}
              </li>
            ))}
          </ul>
        </section>

        <footer className="mt-6 md:mt-8 text-center text-xs md:text-sm text-gray-500 animate-in fade-in duration-500 delay-700">
          Shakti Wallet automatically reads local progress from both games.
        </footer>
      </header>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoomIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounceSubtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }
        @keyframes pulseSlow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .animate-fadeIn { animation: fadeIn 0.6s ease-out; }
        .animate-in { animation-fill-mode: both; }
        .fade-in { animation-name: fadeIn; }
        .zoom-in { animation-name: zoomIn; }
        .animate-slide-in-left { animation: slideInLeft 0.6s ease-out; }
        .animate-slide-in-right { animation: slideInRight 0.6s ease-out; }
        .animate-fade-in-up { animation: fadeInUp 0.5s ease-out; }
        .animate-fade-in-down { animation: fadeInDown 0.5s ease-out; }
        .animate-bounce-subtle { animation: bounceSubtle 2s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulseSlow 3s ease-in-out infinite; }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
        .delay-600 { animation-delay: 0.6s; }
        .delay-700 { animation-delay: 0.7s; }
        .fade-in-50 { animation-duration: 0.5s; }
        .duration-500 { animation-duration: 0.5s; }
        .duration-700 { animation-duration: 0.7s; }
        .duration-1000 { transition-duration: 1s; }
        .slide-in-from-top-4 { --tw-translate-y: -1rem; }
      `}</style>
    </div>
  );
}